import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Rocket, Sparkles, X } from "lucide-react";
import api from "@/lib/api";
import { cadStr } from "@/lib/pricing";
import { fmtDate } from "@/components/merchant/billing/KeepPriceNotice";
import { UpgradeToProDialog } from "@/components/merchant/billing/UpgradeToProDialog";

const KEY = "cy_plan_benefits_banner_dismissed_2026_10";

// The only way existing Starter/Pro subscribers hear about the new plans (no email, approved 2026-10-30).
export default function PlanBenefitsBanner() {
  const [gf, setGf] = useState(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem(KEY) === "1");
  const [upgrading, setUpgrading] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (hidden) return;
    api.get("/merchant/plan-summary").then((r) => setGf(r.data?.grandfathered || null)).catch(() => setGf(null));
  }, [hidden, reload]);
  if (hidden || !gf) return null;
  const dismiss = () => { localStorage.setItem(KEY, "1"); setHidden(true); };
  const up = gf.upgrade;
  return (
    <div data-testid="plan-benefits-banner"
         className="pf-card mb-6 relative border-emerald-300 bg-gradient-to-r from-emerald-50 via-white to-cyan-50 p-5 pr-12">
      <button onClick={dismiss} aria-label="Dismiss" data-testid="plan-benefits-banner-dismiss"
              className="absolute top-3 right-3 p-1.5 rounded-full text-slate-500 hover:text-slate-900 hover:bg-white transition-colors">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-lg text-slate-900" data-testid="plan-benefits-banner-title">
            New Cyannect plans — your {gf.plan_label} price stays at {cadStr(gf.current_price_cad)}/{gf.period}
          </div>
          <div className="text-sm text-slate-600 mt-1" data-testid="plan-benefits-banner-new">
            {gf.new_for_you?.length ? <>New for you, at no extra cost: <strong>{gf.new_for_you.join("; ")}</strong>. </> : null}
            Verified sales from {fmtDate(gf.fees_from)} use the {gf.current_fee.fee_pct}% or {cadStr(gf.current_fee.fee_minimum_cad)} fee.
          </div>
          {up && (
            <div className="text-sm text-slate-600 mt-1" data-testid="plan-benefits-banner-upgrade">
              Want more? {up.plan_label} adds {up.adds.join(", ")} for {cadStr(up.price_cad)}/{up.period}.
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {up && (
              <button onClick={() => setUpgrading(true)} data-testid="plan-benefits-banner-upgrade-btn"
                      className="pf-btn-primary px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2">
                <Rocket className="w-4 h-4" /> Upgrade to {up.plan_label}
              </button>
            )}
            <Link to="/merchant/billing" data-testid="plan-benefits-banner-cta"
                  className={up ? "text-sm font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
                    : "pf-btn-primary px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"}>
              {up ? "See plan options" : "See your plan"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
      {up && (
        <UpgradeToProDialog open={upgrading} onClose={() => setUpgrading(false)} defaultCadence={up.cadence}
                            onUpgraded={() => setReload((n) => n + 1)} />
      )}
    </div>
  );
}
